import type {FileWithPath} from '@mantine/dropzone';
import type {AlertType} from 'src/shared/hooks/useAlert';
import {ApiEndpoints} from 'src/shared/constants/apiEndpoints';
import ApiCaller from 'src/shared/utils/ApiCaller';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormDocumentUploadFile} from '../FormFileUploadV2';

/** Uploads each file via presigned URL, returning success/failure entries; per-file errors raise an alert. */
export const uploadFileBatch = async (
  files: FileWithPath[],
  setAlert: (message: string, type?: AlertType) => void
): Promise<IFormDocumentUploadFile[]> => {
  const results = await Promise.all(
    files.map(async currentFile => {
      try {
        const resp = await ApiCaller({
          method: 'POST',
          endpointURL: ApiEndpoints.uploadFile,
          data: {filename: currentFile.name, type: currentFile.type},
        });

        const {uploadUrl, accessUrl, key} = resp.data;

        await ApiCaller({
          method: 'PUT',
          endpointURL: '',
          data: currentFile,
          contentType: currentFile.type,
          customEndpointURL: uploadUrl,
        });

        return {
          name: currentFile.name,
          file: currentFile,
          value: accessUrl,
          success: true,
          encodedUrl: encodeURIComponent(accessUrl),
          encodedKey: encodeURIComponent(key),
          type: currentFile.type,
        } as IFormDocumentUploadFile;
      } catch (error) {
        setAlert(parseErrorMessage(error), 'error');

        return {
          name: currentFile.name,
          success: false,
        } as IFormDocumentUploadFile;
      }
    })
  );

  return results;
};
